/**
 * Biblioteka błędów — co już raz poszło nie tak i co z tego wynika.
 *
 * Ekran powstał z jednego zdania: „przy demo kurników pojawiły się błędy,
 * poprawiliśmy je na szybko i teraz nie pamiętam, co było nie tak". Naprawa
 * bez zapisu znika, a wspomnienie po dwóch tygodniach potrafi wskazać właściwy
 * obszar i błędną przyczynę. Zapis w plikach repozytorium tego nie rozwiązał,
 * bo pisze do niego tylko ten, kto i tak siedzi w repozytorium.
 *
 * Karta pokazuje **wniosek**, a nie opis zdarzenia — to jest jedyna rzecz,
 * którą czyta się przed kolejną taką pracą. Historia („co się stało",
 * „dlaczego wyszło inaczej") siedzi w panelu szczegółów, bo czyta się ją raz.
 *
 * Formularz przyjmuje wpis niekompletny. Trzy pola obowiązkowe, reszta
 * opcjonalna — wpis powstaje zaraz po naprawie, kiedy nikt nie ma cierpliwości
 * do ośmiu pól, a walidacja, która wtedy odrzuca, kończy się nie lepszym
 * wpisem, tylko żadnym.
 */

import { useDeferredValue, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { RichTextEditor, RichTextView } from "../components/RichText";
import { Card, Chip, Empty, ErrorState, Loading, SidePanel } from "../components/ui";
import { useSession } from "../hooks/useSession";
import { api } from "../lib/api";
import { LABELS } from "../lib/format";
import type { Lesson, ProductBrief } from "../lib/types";

const DOMAINS = ["technical", "business"] as const;
const SCOPES = ["product", "portfolio", "organization"] as const;
const STATUSES = ["open", "accepted", "archived"] as const;

const SCOPE_HINT: Record<string, string> = {
  product: "powtórzyłoby się tylko w tym produkcie",
  portfolio: "powtórzyłoby się na drugim produkcie",
  organization: "dotyczy sposobu pracy, nie przedmiotu pracy",
};

const STATUS_TONE: Record<string, "good" | "warn" | "quiet"> = {
  open: "warn",
  accepted: "good",
  archived: "quiet",
};

type Draft = {
  title: string;
  what_happened: string;
  conclusion: string;
  expectation: string;
  cause: string;
  domain: string;
  scope: string;
  status: string;
  product: string;
  tags: string;
  checklist: string;
  source: string;
  occurred_on: string;
};

/* Dzisiejsza data w czasie lokalnym. `toISOString()` przelicza na UTC i w Polsce
   między północą a 1:00/2:00 zwraca wczorajszy dzień. */
function today(): string {
  const teraz = new Date();
  const miesiac = String(teraz.getMonth() + 1).padStart(2, "0");
  const dzien = String(teraz.getDate()).padStart(2, "0");
  return `${teraz.getFullYear()}-${miesiac}-${dzien}`;
}

function emptyDraft(): Draft {
  return {
    title: "",
    what_happened: "",
    conclusion: "",
    expectation: "",
    cause: "",
    domain: "technical",
    scope: "product",
    status: "open",
    product: "",
    tags: "",
    checklist: "",
    source: "",
    occurred_on: today(),
  };
}

function draftFrom(lesson: Lesson): Draft {
  return {
    title: lesson.title,
    what_happened: lesson.what_happened,
    conclusion: lesson.conclusion,
    expectation: lesson.expectation ?? "",
    cause: lesson.cause ?? "",
    domain: lesson.domain,
    scope: lesson.scope,
    status: lesson.status,
    product: lesson.product_slug ?? "",
    tags: lesson.tags.join(", "),
    checklist: lesson.checklist ?? "",
    source: lesson.source ?? "",
    occurred_on: lesson.occurred_on.slice(0, 10),
  };
}

/** Etykiety rozdzielane przecinkiem. Normalizację i tak robi backend. */
function parseTags(raw: string): string[] {
  return raw
    .split(",")
    .map((tag) => tag.trim())
    .filter(Boolean);
}

function payload(draft: Draft) {
  return {
    title: draft.title.trim(),
    what_happened: draft.what_happened.trim(),
    conclusion: draft.conclusion.trim(),
    expectation: draft.expectation.trim() || null,
    cause: draft.cause.trim() || null,
    domain: draft.domain,
    scope: draft.scope,
    status: draft.status,
    product: draft.product || null,
    tags: parseTags(draft.tags),
    checklist: draft.checklist.trim() || null,
    source: draft.source.trim() || null,
    occurred_on: draft.occurred_on,
  };
}

export function LessonsPage() {
  const queryClient = useQueryClient();
  const session = useSession();
  const canWrite = session.data?.can_write ?? false;

  const [filters, setFilters] = useState({ q: "", product: "", domain: "", scope: "", status: "" });
  const [tag, setTag] = useState("");
  // Szukanie po treści nie musi gonić klawiatury — odroczenie zdejmuje
  // żądanie z każdego naciśnięcia klawisza i nic nie kosztuje przy pisaniu.
  const needle = useDeferredValue(filters.q);

  const [open, setOpen] = useState<Lesson | "new" | null>(null);
  const [draft, setDraft] = useState<Draft>(emptyDraft);

  const params = new URLSearchParams();
  if (needle.trim()) params.set("q", needle.trim());
  if (filters.product) params.set("product", filters.product);
  if (filters.domain) params.set("domain", filters.domain);
  if (filters.scope) params.set("scope", filters.scope);
  if (filters.status) params.set("status", filters.status);
  if (tag) params.set("tag", tag);
  const query = params.toString();

  const lessons = useQuery({
    queryKey: ["lessons", query],
    queryFn: () => api.get<{ lessons: Lesson[]; tags: string[] }>(`/lessons${query ? `?${query}` : ""}`),
  });

  const products = useQuery({
    queryKey: ["products", "wszystkie"],
    queryFn: () => api.get<{ products: ProductBrief[] }>("/products"),
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ["lessons"] });
  const close = () => setOpen(null);

  const create = useMutation({
    mutationFn: () => api.post<{ lesson: Lesson }>("/lessons", payload(draft)),
    onSuccess: () => {
      refresh();
      close();
    },
  });

  const update = useMutation({
    mutationFn: (id: string) => api.patch<{ lesson: Lesson }>(`/lessons/${id}`, payload(draft)),
    onSuccess: () => {
      refresh();
      close();
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/lessons/${id}`),
    onSuccess: () => {
      refresh();
      close();
    },
  });

  const startNew = () => {
    setDraft(emptyDraft());
    setOpen("new");
  };

  const startEdit = (lesson: Lesson) => {
    setDraft(draftFrom(lesson));
    setOpen(lesson);
  };

  const rows = lessons.data?.lessons ?? [];
  const knownTags = lessons.data?.tags ?? [];
  const saving = create.isPending || update.isPending;
  const saveError = create.error ?? update.error ?? remove.error;
  const complete =
    draft.title.trim() && draft.what_happened.trim() && draft.conclusion.trim();

  return (
    <>
      <header className="page-head">
        <div>
          <p className="page-head__eyebrow">Portfolio</p>
          <h1>Błędy</h1>
          <p className="page-head__sub">
            Co już raz poszło nie tak — w narzędziu i w decyzji. Zapisane po to, żeby przed
            podobną pracą dało się to przeczytać, a nie odtwarzać z pamięci.
          </p>
        </div>
        {canWrite && (
          <button type="button" className="btn btn--primary" onClick={startNew}>
            Zapisz błąd
          </button>
        )}
      </header>

      <div className="stack">
        <Card title="Szukaj" note="Przeszukuje też opis i przyczynę, nie tylko tytuły">
          <div className="form-grid">
            <label>
              <span>Treść</span>
              <input
                type="search"
                placeholder="cache, demo, faktura…"
                value={filters.q}
                onChange={(event) => setFilters({ ...filters, q: event.target.value })}
              />
            </label>
            <label>
              <span>Produkt</span>
              <select
                value={filters.product}
                onChange={(event) => setFilters({ ...filters, product: event.target.value })}
              >
                <option value="">wszystkie</option>
                {products.data?.products.map((product) => (
                  <option key={product.slug} value={product.slug}>
                    {product.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Rodzaj</span>
              <select
                value={filters.domain}
                onChange={(event) => setFilters({ ...filters, domain: event.target.value })}
              >
                <option value="">techniczne i biznesowe</option>
                {DOMAINS.map((value) => (
                  <option key={value} value={value}>
                    {LABELS.lessonDomain[value]}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Zasięg</span>
              <select
                value={filters.scope}
                onChange={(event) => setFilters({ ...filters, scope: event.target.value })}
              >
                <option value="">dowolny</option>
                {SCOPES.map((value) => (
                  <option key={value} value={value}>
                    {LABELS.lessonScope[value]}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Status</span>
              <select
                value={filters.status}
                onChange={(event) => setFilters({ ...filters, status: event.target.value })}
              >
                <option value="">bez archiwum</option>
                {STATUSES.map((value) => (
                  <option key={value} value={value}>
                    {LABELS.lessonStatus[value]}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {knownTags.length > 0 && (
            <p className="lesson-tags">
              <button
                type="button"
                className={tag ? "btn btn--quiet" : "btn btn--quiet is-on"}
                onClick={() => setTag("")}
              >
                wszystkie etykiety
              </button>
              {knownTags.map((value) => (
                <button
                  key={value}
                  type="button"
                  className={tag === value ? "btn btn--quiet is-on" : "btn btn--quiet"}
                  onClick={() => setTag(tag === value ? "" : value)}
                >
                  {value}
                </button>
              ))}
            </p>
          )}
        </Card>

        {lessons.isPending ? (
          <Loading label="Wczytuję bibliotekę…" />
        ) : lessons.isError ? (
          <ErrorState error={lessons.error} />
        ) : rows.length === 0 ? (
          <Empty
            title={query ? "Nic nie pasuje" : "Nic tu jeszcze nie ma"}
            hint={
              query
                ? "Żaden wpis nie spełnia tych warunków. Zdejmij filtr albo poszukaj innego słowa."
                : "Pierwszy wpis warto zrobić zaraz po naprawie — wtedy jeszcze wiadomo, co było nie tak."
            }
          />
        ) : (
          <div className="lessons">
            {rows.map((lesson) => (
              <article key={lesson.id} className="lesson">
                <header className="lesson__head">
                  <div>
                    <p className="lesson__meta">
                      <span className="mono">{lesson.occurred_on.slice(0, 10)}</span>
                      {lesson.product_slug && <span>{lesson.product_slug}</span>}
                      {lesson.origin === "agent" && <span>zapis agenta</span>}
                    </p>
                    <h3>{lesson.title}</h3>
                  </div>
                  <button
                    type="button"
                    className="btn btn--quiet"
                    onClick={() => startEdit(lesson)}
                  >
                    {canWrite ? "Otwórz" : "Przeczytaj"}
                  </button>
                </header>

                {/* Wniosek na wierzchu, historia w panelu. To jest jedyne
                    zdanie, które ktoś przeczyta przed kolejną taką pracą. */}
                <div className="lesson__conclusion">
                  <RichTextView value={lesson.conclusion} />
                </div>

                <footer className="lesson__foot">
                  <Chip tone={STATUS_TONE[lesson.status] ?? "quiet"}>
                    {LABELS.lessonStatus[lesson.status] ?? lesson.status}
                  </Chip>
                  <Chip tone={lesson.domain === "business" ? "info" : "quiet"}>
                    {LABELS.lessonDomain[lesson.domain] ?? lesson.domain}
                  </Chip>
                  <Chip tone="quiet" title={SCOPE_HINT[lesson.scope]}>
                    {LABELS.lessonScope[lesson.scope] ?? lesson.scope}
                  </Chip>
                  {lesson.checklist && <Chip tone="quiet">lista: {lesson.checklist}</Chip>}
                  {lesson.tags.map((value) => (
                    <button
                      key={value}
                      type="button"
                      className="lesson__tag"
                      onClick={() => setTag(value)}
                    >
                      {value}
                    </button>
                  ))}
                </footer>
              </article>
            ))}
          </div>
        )}
      </div>

      {open && (
        <SidePanel
          title={open === "new" ? "Nowy wpis" : open.title}
          onClose={close}
          footer={
            canWrite ? (
              <>
                <button
                  type="button"
                  className="btn btn--primary"
                  disabled={saving || !complete}
                  onClick={() => (open === "new" ? create.mutate() : update.mutate(open.id))}
                >
                  {saving ? "Zapisuję…" : "Zapisz"}
                </button>
                {open !== "new" && (
                  <button
                    type="button"
                    className="btn btn--quiet"
                    disabled={remove.isPending}
                    onClick={() => remove.mutate(open.id)}
                  >
                    Usuń wpis
                  </button>
                )}
              </>
            ) : null
          }
        >
          <div className="form-grid">
            <label className="form-grid__wide">
              <span>Tytuł</span>
              <input
                required
                readOnly={!canWrite}
                placeholder="Demo przebudowywane co noc"
                value={draft.title}
                onChange={(event) => setDraft({ ...draft, title: event.target.value })}
              />
            </label>

            <div className="form-grid__wide field">
              <p className="field__label">Co się stało</p>
              {canWrite ? (
                <RichTextEditor
                  label="Co się stało"
                  placeholder="Nocne zadanie kasowało fermę pokazową i budowało ją od zera."
                  value={draft.what_happened}
                  onChange={(next) => setDraft({ ...draft, what_happened: next })}
                />
              ) : (
                <RichTextView value={draft.what_happened} />
              )}
              <small>Fakty. Bez oceny i bez szukania winnego.</small>
            </div>

            <div className="form-grid__wide field">
              <p className="field__label">Wniosek</p>
              {canWrite ? (
                <RichTextEditor
                  label="Wniosek"
                  placeholder="Dane pokazowe utrzymuje się przez dopisywanie, nie przez odtwarzanie."
                  value={draft.conclusion}
                  onChange={(next) => setDraft({ ...draft, conclusion: next })}
                />
              ) : (
                <RichTextView value={draft.conclusion} />
              )}
              <small>Jedno zdanie, które da się zastosować następnym razem.</small>
            </div>

            <label>
              <span>Kiedy</span>
              <input
                type="date"
                readOnly={!canWrite}
                value={draft.occurred_on}
                onChange={(event) => setDraft({ ...draft, occurred_on: event.target.value })}
              />
              <small>Data zdarzenia, nie data zapisania.</small>
            </label>

            <label>
              <span>Produkt</span>
              <select
                disabled={!canWrite}
                value={draft.product}
                onChange={(event) => setDraft({ ...draft, product: event.target.value })}
              >
                <option value="">całe portfolio</option>
                {products.data?.products.map((product) => (
                  <option key={product.slug} value={product.slug}>
                    {product.name}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <span>Rodzaj</span>
              <select
                disabled={!canWrite}
                value={draft.domain}
                onChange={(event) => setDraft({ ...draft, domain: event.target.value })}
              >
                {DOMAINS.map((value) => (
                  <option key={value} value={value}>
                    {LABELS.lessonDomain[value]}
                  </option>
                ))}
              </select>
              <small>Błąd w narzędziu czy w decyzji.</small>
            </label>

            <label>
              <span>Zasięg</span>
              <select
                disabled={!canWrite}
                value={draft.scope}
                onChange={(event) => setDraft({ ...draft, scope: event.target.value })}
              >
                {SCOPES.map((value) => (
                  <option key={value} value={value}>
                    {LABELS.lessonScope[value]}
                  </option>
                ))}
              </select>
              <small>{SCOPE_HINT[draft.scope]}</small>
            </label>

            <label>
              <span>Status</span>
              <select
                disabled={!canWrite}
                value={draft.status}
                onChange={(event) => setDraft({ ...draft, status: event.target.value })}
              >
                {STATUSES.map((value) => (
                  <option key={value} value={value}>
                    {LABELS.lessonStatus[value]}
                  </option>
                ))}
              </select>
              <small>Archiwum znika z listy, ale zostaje w bazie.</small>
            </label>

            <label>
              <span>Etykiety</span>
              <input
                readOnly={!canWrite}
                placeholder="demo, wdrozenie"
                value={draft.tags}
                onChange={(event) => setDraft({ ...draft, tags: event.target.value })}
              />
              <small>
                {knownTags.length ? `już używane: ${knownTags.join(", ")}` : "po przecinku"}
              </small>
            </label>

            <label>
              <span>Lista kontrolna</span>
              <input
                readOnly={!canWrite}
                placeholder="konto-demo"
                value={draft.checklist}
                onChange={(event) => setDraft({ ...draft, checklist: event.target.value })}
              />
              <small>Slug pliku z `.ai-org/doswiadczenie/listy-kontrolne/`.</small>
            </label>

            <div className="form-grid__wide field">
              <p className="field__label">Czego się spodziewaliśmy</p>
              {canWrite ? (
                <RichTextEditor
                  label="Czego się spodziewaliśmy"
                  value={draft.expectation}
                  onChange={(next) => setDraft({ ...draft, expectation: next })}
                />
              ) : (
                <RichTextView value={draft.expectation} />
              )}
              <small>Bez tego nie ma lekcji, jest opis. Lekcja mieszka w różnicy.</small>
            </div>

            <div className="form-grid__wide field">
              <p className="field__label">Dlaczego wyszło inaczej</p>
              {canWrite ? (
                <RichTextEditor
                  label="Dlaczego wyszło inaczej"
                  value={draft.cause}
                  onChange={(next) => setDraft({ ...draft, cause: next })}
                />
              ) : (
                <RichTextView value={draft.cause} />
              )}
              <small>Przyczyna, nie objaw.</small>
            </div>

            <label className="form-grid__wide">
              <span>Źródło</span>
              <input
                readOnly={!canWrite}
                placeholder="commit 6ac46d0, rozmowa z 7 września"
                value={draft.source}
                onChange={(event) => setDraft({ ...draft, source: event.target.value })}
              />
            </label>

            {open !== "new" && (
              <p className="form-grid__wide dim">
                Zapisał: {open.author_name ?? "nieznany"}
                {open.origin_ref ? ` · z pliku ${open.origin_ref}` : ""}
              </p>
            )}

            {saveError && (
              <p className="form-error form-grid__wide" role="alert">
                {saveError instanceof Error ? saveError.message : "Nie udało się zapisać"}
              </p>
            )}
          </div>
        </SidePanel>
      )}
    </>
  );
}
